import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import FormHelperText from "@material-ui/core/FormHelperText";
import Button from "@material-ui/core/Button";
import Layout from "../rx/layout";
import { Api } from '../Api';
import Challenge from "../auth/challenge";
import { AuthContext } from "../auth/authContext";
import OtpChallenge from "../auth/otp";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import { LoadingButton } from "@mui/lab";
import { parseDateInput, isValidDate } from "../common/dateUtils";

const GENDER_OPTIONS = ['Male', 'Female', 'Other', 'Prefer not to say'];
const TIMEZONE_OPTIONS = ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix', 'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu'];

const getErrorMessage = (error) => {
  if (error.response) {
    const status = error.response.status;

    if (status === 410) {
      return "This form is no longer available.";
    } else if (status === 404) {
      return "We couldn't find the form you're looking for.";
    } else if (status >= 500) {
      return "The server is experiencing issues. Please try again later.";
    } else {
      return "Unable to load form data. Please try again.";
    }
  } else {
    // Network error (timeout, connection failure)
    return "Network connection error. Please check your internet connection.";
  }
};

export default function AddressVerificationForm() {
  const [backdropOpen, setBackdropOpen] = useState(false);
  const [formData, setFormData] = useState(null);
  const [hubFields, setHubFields] = useState({});
  const [hubFieldVisibility, setHubFieldVisibility] = useState({})
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const authContext = useContext(AuthContext);
  const location = useLocation();

  function useQuery() {
    return new URLSearchParams(location.search);
  }

  const query = useQuery();
  const sbnKey = query.get("s");
  const dcId = query.get("i");

  useEffect(() => {
    const getLogo = () => {
      if (sbnKey) {
        Api.get(`/hub/getlogo?s=${sbnKey}`)
          .then((response) => authContext.setOrgData(response.data))
          .catch((_err) => { });
      }
    };
    getLogo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sbnKey]);

  useEffect(() => {
    const getFormData = () => {
      if (sbnKey && authContext?.isAuthenticated) {
        setBackdropOpen(true);
        Api.get(`/hubform/get?s=${sbnKey}`)
          .then((response) => {
            setFormData(response.data);
            setHubFields(response.data.hubFields || {});
            const visibility = response.data.hubFieldVisibility || {};
            setHubFieldVisibility(visibility);

            // Debug: check for field/visibility mismatches
            if (Object.keys(visibility).length > 0) {
              debugFieldVisibilityMismatches(visibility);
            }
          })
          .catch((err) => {
            setError(getErrorMessage(err));
          })
          .finally(() => setBackdropOpen(false));
      }
    };
    getFormData();
  }, [sbnKey, authContext?.isAuthenticated]);

  const handleFieldChange = (field, value) => {
    setHubFields(prev => ({
      ...prev,
      [field]: value
    }));
    // Validation state persists; re-evaluated on next submit
  };

  // Debug: check for mismatches between fields and visibility settings
  const debugFieldVisibilityMismatches = (visibilitySettings) => {
    const allFieldNames = [
      'firstName', 'lastName', 'dob', 'gender',
      'mobilePhone', 'otherPhone', 'email',
      'address1', 'address2', 'city', 'state', 'postalCode', 'locationName',
      'proxyName', 'proxyPhone',
      'doctorFirstName', 'doctorLastName', 'doctorPhone',
      'guarantorName', 'guarantorPhone',
      'insurancePrimary', 'insuranceSecondary'
    ];

    const visibilityKeys = Object.keys(visibilitySettings);

    // Find fields without visibility markings (will default to "optional")
    const fieldsWithoutVisibility = allFieldNames.filter(
      field => !visibilityKeys.includes(field)
    );

    // Find visibility markings without corresponding fields
    const visibilityWithoutFields = visibilityKeys.filter(
      key => !allFieldNames.includes(key)
    );

    if (fieldsWithoutVisibility.length > 0 || visibilityWithoutFields.length > 0) {
      // eslint-disable-next-line no-console
      console.group('🔍 Field Visibility Mismatches');

      if (fieldsWithoutVisibility.length > 0) {
        // eslint-disable-next-line no-console
        console.warn(
          `Fields without visibility markings (defaulting to "optional"):`,
          fieldsWithoutVisibility
        );
      }

      if (visibilityWithoutFields.length > 0) {
        // eslint-disable-next-line no-console
        console.error(
          `Visibility markings for non-existent fields:`,
          visibilityWithoutFields
        );
      }

      // eslint-disable-next-line no-console
      console.groupEnd();
    } else {
      // eslint-disable-next-line no-console
      console.log('✅ All fields have matching visibility settings');
    }
  };

  // Helper: get field visibility setting with "optional" as default
  const getFieldVisibility = (fieldName) => {
    return hubFieldVisibility[fieldName] || "optional";
  };

  // Helper: check if field should be rendered
  const isFieldVisible = (fieldName) => {
    return getFieldVisibility(fieldName) !== "hidden";
  };

  // Helper: check if field is required
  const isFieldRequired = (fieldName) => {
    return getFieldVisibility(fieldName) === "required";
  };

  // Helper: get all required fields that are currently empty
  const getEmptyRequiredFields = () => {
    const allFieldNames = [
      'firstName', 'lastName', 'dob', 'gender',
      'mobilePhone', 'otherPhone', 'email',
      'address1', 'address2', 'city', 'state', 'postalCode', 'locationName',
      'proxyName', 'proxyPhone',
      'doctorFirstName', 'doctorLastName', 'doctorPhone',
      'guarantorName', 'guarantorPhone',
      'insurancePrimary', 'insuranceSecondary'
    ];

    return allFieldNames.filter(fieldName =>
      isFieldRequired(fieldName) &&
      isFieldVisible(fieldName) &&
      (!hubFields[fieldName] || String(hubFields[fieldName]).trim() === '')
    );
  };

  // Helper: validate DOB format (accepts any parseable format)
  const isValidDobFormat = (dob) => {
    if (!dob) return false;
    const parsed = parseDateInput(dob);
    return parsed !== null && isValidDate(parsed);
  };

  // Helper: check if field has validation error (for error display)
  const hasFieldError = (fieldName) => {
    if (!validationAttempted) return false;

    // Check if required field is empty
    if (isFieldRequired(fieldName) && (!hubFields[fieldName] || String(hubFields[fieldName]).trim() === '')) {
      return true;
    }

    // Special validation for DOB field
    if (fieldName === 'dob' && hubFields.dob && !isValidDobFormat(hubFields.dob)) {
      return true;
    }

    return false;
  };

  // Helper: get error message for field
  const getFieldErrorMessage = (fieldName) => {
    if (fieldName === 'dob' && hubFields.dob && !isValidDobFormat(hubFields.dob)) {
      return 'Please use format: YYYY-MM-DD';
    }
    return 'This field is required';
  };


  const handleReset = () => {
    setHubFields(formData.hubFields || {});
    setError(null);
    setValidationAttempted(false);
  };

  // Helper: convert field name to readable label
  const getFieldLabel = (fieldName) => {
    const labels = {
      firstName: 'First Name',
      lastName: 'Last Name',
      dob: 'Date of Birth',
      gender: 'Gender',
      mobilePhone: 'Mobile Phone',
      otherPhone: 'Other Phone',
      email: 'Email',
      address1: 'Address Line 1',
      address2: 'Address Line 2',
      city: 'City',
      state: 'State',
      postalCode: 'Postal Code',
      locationName: 'Location/Timezone',
      proxyName: 'Proxy Name',
      proxyPhone: 'Proxy Phone',
      doctorFirstName: 'Doctor First Name',
      doctorLastName: 'Doctor Last Name',
      doctorPhone: 'Doctor Phone',
      guarantorName: 'Guarantor Name',
      guarantorPhone: 'Guarantor Phone',
      insurancePrimary: 'Primary Insurance',
      insuranceSecondary: 'Secondary Insurance'
    };
    return labels[fieldName] || fieldName;
  };

  const handleSubmit = () => {
    // Clear previous errors before re-validating
    setError(null);

    // Normalize DOB first before any validation
    let fieldsToValidate = { ...hubFields };
    let dobParseSuccess = true;

    if (isFieldVisible('dob') && fieldsToValidate.dob) {
      const parsed = parseDateInput(fieldsToValidate.dob);
      if (parsed && isValidDate(parsed)) {
        fieldsToValidate.dob = parsed;
      } else {
        // Parsing failed - mark as invalid
        dobParseSuccess = false;
      }
    }

    // Collect all validation errors
    const errors = [];

    // Check for missing required fields
    const missingRequired = getEmptyRequiredFields();

    if (missingRequired.length > 0) {
      const fieldLabels = missingRequired.map(getFieldLabel).join(', ');
      errors.push(`Please fill in the following required fields: ${fieldLabels}`);
    }

    // Check DOB format
    if (isFieldVisible('dob') && fieldsToValidate.dob && !dobParseSuccess) {
      errors.push(`Please correct Date of Birth (use format: YYYY-MM-DD)`);
    }

    // If there are any errors, show them all
    if (errors.length > 0) {
      setValidationAttempted(true);
      setError(errors.join('. '));
      return;
    }

    setLoading(true);
    setError(null);

    // Use the already-normalized fields
    let normalizedFields = fieldsToValidate;

    // Filter out hidden fields from payload
    const visibleFields = Object.keys(normalizedFields).reduce((acc, key) => {
      if (isFieldVisible(key)) {
        acc[key] = normalizedFields[key];
      }
      return acc;
    }, {});

    const payload = {
      _id: formData._id,
      subscriberKey: formData.subscriberKey,
      subscriberPhone: formData.subscriberPhone,
      subscriptionKey: formData.subscriptionKey,
      hubFields: visibleFields
    };

    Api.post('/hubform/update', payload)
      .then(() => {
        setSubmitted(true);
      })
      .catch((_err) => {
        setError("Unable to submit form. Please try again.");
      })
      .finally(() => setLoading(false));
  };

  return (
    <div>
      <Backdrop open={backdropOpen} style={{ zIndex: 10 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {!authContext?.isDobAuthenticated && authContext.orgData?.loginType.includes("DOB") &&
        <Challenge subscriberKey={sbnKey} dcId={dcId} />}

      {((authContext?.isDobAuthenticated && !authContext?.isOtpAuthenticated && authContext.orgData && authContext.orgData?.loginType.includes("OTP")) ||
        (!authContext?.isOtpAuthenticated && authContext.orgData?.loginType === "OTP")) &&
        <OtpChallenge subscriberKey={sbnKey} dcId={dcId} />}

      {authContext?.isAuthenticated && !submitted &&
        <Layout orgData={authContext.orgData}>
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} md={8}>
              <Paper style={{ padding: "20px" }}>
                {error && !formData ? (
                  <>
                    <Typography variant="h5" gutterBottom>
                      Address Verification
                    </Typography>
                    <Typography variant="body2" color="error" paragraph>
                      {error}
                    </Typography>
                  </>
                ) : formData && (
                  <>
                    {formData.formMetadata?.formTitle && (
                      <Typography variant="h5" gutterBottom>
                        {formData.formMetadata.formTitle}
                      </Typography>
                    )}
                    <Typography variant="body1" paragraph>
                      {formData.formMetadata?.formInstructions}
                    </Typography>

                <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                <Grid container spacing={2}>
                  {isFieldVisible('firstName') && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="First Name"
                        value={hubFields.firstName || ''}
                        onChange={(e) => handleFieldChange('firstName', e.target.value)}
                        fullWidth
                        variant="outlined"
                        required={isFieldRequired('firstName')}
                        error={hasFieldError('firstName')}
                        helperText={hasFieldError('firstName') ? 'This field is required' : ''}
                      />
                    </Grid>
                  )}
                  {isFieldVisible('lastName') && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Last Name"
                        value={hubFields.lastName || ''}
                        onChange={(e) => handleFieldChange('lastName', e.target.value)}
                        fullWidth
                        variant="outlined"
                        required={isFieldRequired('lastName')}
                        error={hasFieldError('lastName')}
                        helperText={hasFieldError('lastName') ? 'This field is required' : ''}
                      />
                    </Grid>
                  )}
                  {isFieldVisible('dob') && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Date of Birth"
                        value={hubFields.dob || ''}
                        onChange={(e) => handleFieldChange('dob', e.target.value)}
                        fullWidth
                        variant="outlined"
                        placeholder="YYYY-MM-DD or MM-DD-YY"
                        inputProps={{
                          inputMode: "numeric"
                        }}
                        required={isFieldRequired('dob')}
                        error={hasFieldError('dob')}
                        helperText={hasFieldError('dob') ? getFieldErrorMessage('dob') : ''}
                      />
                    </Grid>
                  )}
                  {isFieldVisible('gender') && (
                    <Grid item xs={12} sm={6}>
                      <FormControl
                        fullWidth
                        variant="outlined"
                        required={isFieldRequired('gender')}
                        error={hasFieldError('gender')}
                      >
                        <InputLabel>Gender</InputLabel>
                        <Select
                          label="Gender"
                          value={hubFields.gender || ''}
                          onChange={(e) => handleFieldChange('gender', e.target.value)}
                        >
                          {hubFields.gender && !GENDER_OPTIONS.includes(hubFields.gender) && (
                            <MenuItem value={hubFields.gender}>{hubFields.gender}</MenuItem>
                          )}
                          {GENDER_OPTIONS.map(option => (
                            <MenuItem key={option} value={option}>{option}</MenuItem>
                          ))}
                        </Select>
                        {hasFieldError('gender') && (
                          <FormHelperText>This field is required</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                  {isFieldVisible('mobilePhone') && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Mobile Phone"
                        value={hubFields.mobilePhone || ''}
                        onChange={(e) => handleFieldChange('mobilePhone', e.target.value)}
                        fullWidth
                        variant="outlined"
                        required={isFieldRequired('mobilePhone')}
                        error={hasFieldError('mobilePhone')}
                        helperText={hasFieldError('mobilePhone') ? 'This field is required' : ''}
                      />
                    </Grid>
                  )}
                  {isFieldVisible('otherPhone') && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Other Phone"
                        value={hubFields.otherPhone || ''}
                        onChange={(e) => handleFieldChange('otherPhone', e.target.value)}
                        fullWidth
                        variant="outlined"
                        required={isFieldRequired('otherPhone')}
                        error={hasFieldError('otherPhone')}
                        helperText={hasFieldError('otherPhone') ? 'This field is required' : ''}
                      />
                    </Grid>
                  )}
                  {isFieldVisible('email') && (
                    <Grid item xs={12}>
                      <TextField
                        label="Email"
                        value={hubFields.email || ''}
                        onChange={(e) => handleFieldChange('email', e.target.value)}
                        fullWidth
                        variant="outlined"
                        type="email"
                        required={isFieldRequired('email')}
                        error={hasFieldError('email')}
                        helperText={hasFieldError('email') ? 'This field is required' : ''}
                      />
                    </Grid>
                  )}
                  {isFieldVisible('address1') && (
                    <Grid item xs={12}>
                      <TextField
                        label="Address Line 1"
                        value={hubFields.address1 || ''}
                        onChange={(e) => handleFieldChange('address1', e.target.value)}
                        fullWidth
                        variant="outlined"
                        required={isFieldRequired('address1')}
                        error={hasFieldError('address1')}
                        helperText={hasFieldError('address1') ? 'This field is required' : ''}
                      />
                    </Grid>
                  )}
                  {isFieldVisible('address2') && (
                    <Grid item xs={12}>
                      <TextField
                        label="Address Line 2"
                        value={hubFields.address2 || ''}
                        onChange={(e) => handleFieldChange('address2', e.target.value)}
                        fullWidth
                        variant="outlined"
                        required={isFieldRequired('address2')}
                        error={hasFieldError('address2')}
                        helperText={hasFieldError('address2') ? 'This field is required' : ''}
                      />
                    </Grid>
                  )}
                  {isFieldVisible('city') && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="City"
                        value={hubFields.city || ''}
                        onChange={(e) => handleFieldChange('city', e.target.value)}
                        fullWidth
                        variant="outlined"
                        required={isFieldRequired('city')}
                        error={hasFieldError('city')}
                        helperText={hasFieldError('city') ? 'This field is required' : ''}
                      />
                    </Grid>
                  )}
                  {isFieldVisible('state') && (
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="State"
                        value={hubFields.state || ''}
                        onChange={(e) => handleFieldChange('state', e.target.value)}
                        fullWidth
                        variant="outlined"
                        required={isFieldRequired('state')}
                        error={hasFieldError('state')}
                        helperText={hasFieldError('state') ? 'This field is required' : ''}
                      />
                    </Grid>
                  )}
                  {isFieldVisible('postalCode') && (
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Postal Code"
                        value={hubFields.postalCode || ''}
                        onChange={(e) => handleFieldChange('postalCode', e.target.value)}
                        fullWidth
                        variant="outlined"
                        required={isFieldRequired('postalCode')}
                        error={hasFieldError('postalCode')}
                        helperText={hasFieldError('postalCode') ? 'This field is required' : ''}
                      />
                    </Grid>
                  )}
                  {isFieldVisible('locationName') && (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        variant="outlined"
                        required={isFieldRequired('locationName')}
                        error={hasFieldError('locationName')}
                      >
                        <InputLabel>Location/Timezone</InputLabel>
                        <Select
                          label="Location/Timezone"
                          value={hubFields.locationName || ''}
                          onChange={(e) => handleFieldChange('locationName', e.target.value)}
                        >
                          {hubFields.locationName && !TIMEZONE_OPTIONS.includes(hubFields.locationName) && (
                            <MenuItem value={hubFields.locationName}>{hubFields.locationName}</MenuItem>
                          )}
                          <MenuItem value="America/New_York">Eastern (America/New_York)</MenuItem>
                          <MenuItem value="America/Chicago">Central (America/Chicago)</MenuItem>
                          <MenuItem value="America/Denver">Mountain (America/Denver)</MenuItem>
                          <MenuItem value="America/Phoenix">Arizona (America/Phoenix)</MenuItem>
                          <MenuItem value="America/Los_Angeles">Pacific (America/Los_Angeles)</MenuItem>
                          <MenuItem value="America/Anchorage">Alaska (America/Anchorage)</MenuItem>
                          <MenuItem value="Pacific/Honolulu">Hawaii (Pacific/Honolulu)</MenuItem>
                        </Select>
                        {hasFieldError('locationName') && (
                          <FormHelperText>This field is required</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                  {isFieldVisible('proxyName') && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Proxy Name"
                        value={hubFields.proxyName || ''}
                        onChange={(e) => handleFieldChange('proxyName', e.target.value)}
                        fullWidth
                        variant="outlined"
                        required={isFieldRequired('proxyName')}
                        error={hasFieldError('proxyName')}
                        helperText={hasFieldError('proxyName') ? 'This field is required' : ''}
                      />
                    </Grid>
                  )}
                  {isFieldVisible('proxyPhone') && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Proxy Phone"
                        value={hubFields.proxyPhone || ''}
                        onChange={(e) => handleFieldChange('proxyPhone', e.target.value)}
                        fullWidth
                        variant="outlined"
                        required={isFieldRequired('proxyPhone')}
                        error={hasFieldError('proxyPhone')}
                        helperText={hasFieldError('proxyPhone') ? 'This field is required' : ''}
                      />
                    </Grid>
                  )}
                  {isFieldVisible('doctorFirstName') && (
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Doctor First Name"
                        value={hubFields.doctorFirstName || ''}
                        onChange={(e) => handleFieldChange('doctorFirstName', e.target.value)}
                        fullWidth
                        variant="outlined"
                        required={isFieldRequired('doctorFirstName')}
                        error={hasFieldError('doctorFirstName')}
                        helperText={hasFieldError('doctorFirstName') ? 'This field is required' : ''}
                      />
                    </Grid>
                  )}
                  {isFieldVisible('doctorLastName') && (
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Doctor Last Name"
                        value={hubFields.doctorLastName || ''}
                        onChange={(e) => handleFieldChange('doctorLastName', e.target.value)}
                        fullWidth
                        variant="outlined"
                        required={isFieldRequired('doctorLastName')}
                        error={hasFieldError('doctorLastName')}
                        helperText={hasFieldError('doctorLastName') ? 'This field is required' : ''}
                      />
                    </Grid>
                  )}
                  {isFieldVisible('doctorPhone') && (
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Doctor Phone"
                        value={hubFields.doctorPhone || ''}
                        onChange={(e) => handleFieldChange('doctorPhone', e.target.value)}
                        fullWidth
                        variant="outlined"
                        required={isFieldRequired('doctorPhone')}
                        error={hasFieldError('doctorPhone')}
                        helperText={hasFieldError('doctorPhone') ? 'This field is required' : ''}
                      />
                    </Grid>
                  )}
                  {isFieldVisible('guarantorName') && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Guarantor Name"
                        value={hubFields.guarantorName || ''}
                        onChange={(e) => handleFieldChange('guarantorName', e.target.value)}
                        fullWidth
                        variant="outlined"
                        required={isFieldRequired('guarantorName')}
                        error={hasFieldError('guarantorName')}
                        helperText={hasFieldError('guarantorName') ? 'This field is required' : ''}
                      />
                    </Grid>
                  )}
                  {isFieldVisible('guarantorPhone') && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Guarantor Phone"
                        value={hubFields.guarantorPhone || ''}
                        onChange={(e) => handleFieldChange('guarantorPhone', e.target.value)}
                        fullWidth
                        variant="outlined"
                        required={isFieldRequired('guarantorPhone')}
                        error={hasFieldError('guarantorPhone')}
                        helperText={hasFieldError('guarantorPhone') ? 'This field is required' : ''}
                      />
                    </Grid>
                  )}
                  {isFieldVisible('insurancePrimary') && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Primary Insurance"
                        value={hubFields.insurancePrimary || ''}
                        onChange={(e) => handleFieldChange('insurancePrimary', e.target.value)}
                        fullWidth
                        variant="outlined"
                        required={isFieldRequired('insurancePrimary')}
                        error={hasFieldError('insurancePrimary')}
                        helperText={hasFieldError('insurancePrimary') ? 'This field is required' : ''}
                      />
                    </Grid>
                  )}
                  {isFieldVisible('insuranceSecondary') && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Secondary Insurance"
                        value={hubFields.insuranceSecondary || ''}
                        onChange={(e) => handleFieldChange('insuranceSecondary', e.target.value)}
                        fullWidth
                        variant="outlined"
                        required={isFieldRequired('insuranceSecondary')}
                        error={hasFieldError('insuranceSecondary')}
                        helperText={hasFieldError('insuranceSecondary') ? 'This field is required' : ''}
                      />
                    </Grid>
                  )}
                </Grid>

                {formData.formMetadata?.formClosingPrompt && (
                  <Typography variant="body2" paragraph style={{ marginTop: '20px' }}>
                    {formData.formMetadata.formClosingPrompt}
                  </Typography>
                )}

                {error && (
                  <Typography variant="body2" color="error" paragraph style={{ marginTop: '20px' }}>
                    {error}
                  </Typography>
                )}

                <Grid container spacing={2} style={{ marginTop: '20px' }}>
                  <Grid item xs={6}>
                    <Button
                      variant="outlined"
                      color="default"
                      onClick={handleReset}
                      fullWidth
                      disabled={loading}
                    >
                      Reset
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <LoadingButton
                      type="submit"
                      variant="contained"
                      color="primary"
                      loading={loading}
                      fullWidth
                    >
                      Submit
                    </LoadingButton>
                  </Grid>
                </Grid>
                </form>
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Layout>
      }

      {authContext?.isAuthenticated && submitted &&
        <Layout orgData={authContext.orgData}>
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} md={8}>
              <Paper style={{ padding: "20px", textAlign: "center" }}>
                {formData.formMetadata?.formTitle && (
                  <Typography variant="h6" gutterBottom>
                    {formData.formMetadata.formTitle}
                  </Typography>
                )}
                <Typography variant="h5" gutterBottom color="primary">
                  Thank You!
                </Typography>
                <Typography variant="body1" paragraph>
                  Your information has been submitted successfully.
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  You may now close this window.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Layout>
      }
    </div>
  );
}
